/**
 * React Query hook for backend account data.
 * 
 * Uses the centralized account store for state management.
 * - Reads balance from store (populated by useAccountSync)
 * - Mutations update store immediately from HTTP response
 * - No WebSocket/HTTP switching logic - store handles that
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AccountsApi } from "../api-client";
import { useAccountStore, type StoreBalance } from "../store";

/** Account summary type for UI display (backwards compatible) */
export interface AccountSummary {
  collateral: number;
  availableBalance: number;
  usedMargin: number;
  totalPnl: number;
  unrealizedPnl: number;
  realizedPnl: number;
  marginRatio: number;
  accountLeverage: number;
}

export const useBackendAccount = (userId: string | undefined) => {
  const queryClient = useQueryClient();
  
  // Read balance from centralized store (raw data)
  const storeBalance = useAccountStore((state) => state.balance);
  const isInitialized = useAccountStore((state) => state.isInitialized);
  const isLoading = useAccountStore((state) => state.isLoading);

  // Convert store balance to AccountSummary format for backwards compatibility
  const summary: AccountSummary | null = storeBalance
    ? {
        collateral: storeBalance.collateral,
        availableBalance: storeBalance.availableMargin,
        usedMargin: storeBalance.usedMargin,
        totalPnl: storeBalance.unrealizedPnl + storeBalance.realizedPnl,
        unrealizedPnl: storeBalance.unrealizedPnl,
        realizedPnl: storeBalance.realizedPnl,
        marginRatio: storeBalance.marginRatio,
        accountLeverage: storeBalance.accountLeverage,
      }
    : null;

  const createAccountMutation = useMutation({
    mutationFn: () => AccountsApi.createAccount(),
    onSuccess: () => {
      // Trigger a full sync to get the new account data
      queryClient.invalidateQueries({ queryKey: ["account-sync", userId] });
      queryClient.invalidateQueries({ queryKey: ["backend-account", userId] });
    },
  });

  // Refetch function - triggers a full sync
  const refetch = async () => {
    queryClient.invalidateQueries({ queryKey: ["account-sync", userId] });
  };

  return {
    account: null, // Deprecated - use summary instead
    summary,
    isLoading: isLoading || !isInitialized,
    error: null as Error | null,
    refetch,
    createAccount: createAccountMutation.mutate,
    isCreating: createAccountMutation.isPending,
    isWsConnected: isInitialized, // Store is connected when initialized
  };
};

// Re-export the balance type for backwards compatibility
export type { StoreBalance as BackendBalance };
